import { useState, useEffect, useRef, memo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import axios from "axios";
import { apiGet } from "@/lib/apiCache";
import { API, useCart, useAuth } from "@/App";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { OptimizedImage } from "@/components/shared/OptimizedImage";
import { ProductGridSkeleton } from "@/components/shared/Skeletons";
import { useLang } from "@/context/LangContext";
import SEOHead from "@/components/SEOHead";
import { 
  FlaskConical, Shield, FileCheck, CreditCard, Microscope, 
  ChevronRight, ChevronLeft, Search, UserPlus, ShoppingBag, 
  ClipboardCheck, Truck, CheckCircle, ArrowRight, Download,
  X, ShoppingCart, Tag, Sparkles, Mail, Phone, MapPin
} from "lucide-react";
import { toast } from "sonner";
import {
  Counter, AnimatedSection, DNABackground, FeatureCard, TimelineStep,
  getBackgroundPosition, BRAND_IMAGES,
} from "@/pages/home/shared";
import { TrustStrip } from "@/components/shared/TrustStrip";
import { AnalyticalTransparency } from "@/components/shared/AnalyticalTransparency";
import { RecentlyViewed } from "@/components/shared/RecentlyViewed";
import { PromoBanner } from "@/components/shared/PromoBanner";

const LOGO_URL = BRAND_IMAGES.LOGO;
const VIAL_IMAGE = BRAND_IMAGES.VIAL;
const IMG_BRANDED_VIALS = BRAND_IMAGES.BRANDED_VIALS;
const IMG_DNA_HELIX = BRAND_IMAGES.DNA_HELIX;
const IMG_LAB_EQUIPMENT = BRAND_IMAGES.LAB_EQUIPMENT;
const IMG_HERO_DNA = BRAND_IMAGES.HERO_DNA;

// Product Card Component
const ProductCard = ({ product, onQuickView }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { t } = useLang();

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    if (!user) {
      toast.error("Please login to add items to cart");
      navigate("/login");
      return;
    }
    const success = await addToCart(product.id);
    if (success) {
      toast.success(`${product.name} added to cart`);
    }
  };

  return (
    <motion.div
      whileHover={{ y: -8 }}
      className="group relative bg-[#0F1115] border border-white/5 rounded-xl overflow-hidden hover:border-[#00AEEF]/30 transition-all duration-500 min-w-[280px] w-[280px] flex-shrink-0"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden bg-[#0a0b0e] flex items-center justify-center">
        <OptimizedImage 
          src={product.image_url || product.image || "https://images.unsplash.com/photo-1627916665106-48730fbd9e6f?w=400"}
          alt={product.name}
          className="h-full w-full"
          aspectRatio="1/1"
        />
        <div className="product-image-overlay absolute inset-0" />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {product.featured && (
            <span className="px-2 py-1 bg-[#00AEEF] text-white text-xs font-semibold rounded">
              {t("featured_label")}
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3">
          <span className="badge-coa">{t("coa_available")}</span>
        </div>

        {/* Quick View Button */}
        <button
          onClick={() => onQuickView(product)}
          className="absolute bottom-3 right-3 p-2 bg-black/50 backdrop-blur-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity text-white hover:bg-black/70"
          data-testid={`quick-view-${product.id}`}
        >
          <Search className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-lg font-semibold text-white mb-1">{product.name}</h3>
        <p className="text-slate-400 text-sm mb-4 line-clamp-2">{product.short_description}</p>
        
        <div className="flex items-center justify-between">
          {product.has_variants ? (
            <span className="text-[#00AEEF] font-bold text-lg">From ${product.starting_price?.toFixed(2)}</span>
          ) : (
            <span className="text-[#00AEEF] font-bold text-lg">${product.price?.toFixed(2)}</span>
          )}
          <Link
            to={`/product/${product.id}`}
            className="px-3 py-2 text-sm text-white border border-white/10 rounded-lg hover:border-[#00AEEF]/50 transition-colors"
          >
            {product.has_variants ? "Select" : "Details"}
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

// Quick View Modal
const QuickViewModal = ({ product, onClose }) => {
  const { t } = useLang();
  if (!product) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 quickview-overlay"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="glass-dark rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          className="relative h-64 overflow-hidden rounded-t-2xl bg-[#0a0b0e] flex items-center justify-center"
        >
          <OptimizedImage 
            src={product.image_url || product.image || "https://images.unsplash.com/photo-1627916665106-48730fbd9e6f?w=400"}
            alt={product.name}
            className="h-full w-full"
            priority={true}
          />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-8">
          <h2 className="text-2xl font-bold text-white mb-2">{product.name}</h2>
          <p className="text-slate-400 mb-6">{product.description}</p>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            {Object.entries(product.specifications || {}).slice(0, 4).map(([key, value]) => (
              <div key={key} className="bg-white/5 rounded-lg p-3">
                <span className="text-slate-500 text-xs uppercase">{key.replace("_", " ")}</span>
                <p className="text-white font-medium">{value}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-[#00AEEF] font-bold text-2xl">${product.price?.toFixed(2)}</span>
            <Link
              to={`/product/${product.id}`}
              className="btn-primary flex items-center gap-2"
              onClick={onClose}
            >
              {t("view_full_details")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const InteractiveVial = () => {
  const [activeMarker, setActiveMarker] = useState(null);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef(null);
  const { t } = useLang();

  const vialMarkers = [
    {
      id: 1,
      label: t("vial_marker_1_title"),
      description: t("vial_marker_1_desc"),
      icon: Shield,
      position: { top: "15%", left: "68%" }
    },
    {
      id: 2,
      label: t("vial_marker_2_title"),
      description: t("vial_marker_2_desc"),
      icon: FlaskConical,
      position: { top: "55%", left: "72%" }
    },
    {
      id: 3,
      label: t("vial_marker_3_title"),
      description: t("vial_marker_3_desc"),
      icon: FileCheck,
      position: { top: "35%", left: "18%" }
    }
  ];

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const rotateY = ((e.clientX - centerX) / (rect.width / 2)) * 20;
    const rotateX = ((centerY - e.clientY) / (rect.height / 2)) * 10;
    setRotation({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setRotation({ x: 0, y: 0 });
  };

  return (
    <div className="relative max-w-6xl mx-auto" data-testid="interactive-vial">
      <div className="grid lg:grid-cols-5 gap-8 items-center">
        {/* Vial 3D Container - takes 3 cols */}
        <div
          ref={containerRef}
          className="lg:col-span-3 relative flex items-center justify-center py-8"
          onMouseMove={handleMouseMove}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={handleMouseLeave}
          style={{ perspective: "1200px" }}
        >
          {/* Ambient glow */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-72 h-72 bg-[#00AEEF]/15 rounded-full blur-[100px]" />
          </div>
          
          {/* Floor reflection */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-64 h-16 bg-[#00AEEF]/5 blur-[30px] rounded-full" />

          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut" }}
            animate={{
              y: isHovering ? 0 : [0, -12, 0],
            }}
            {...(!isHovering && {
              transition: {
                y: { repeat: Infinity, duration: 3, ease: "easeInOut" },
              },
            })}
            className="relative z-10 cursor-grab active:cursor-grabbing select-none"
            style={{
              transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
              transition: isHovering ? "transform 0.1s ease-out" : "transform 0.6s ease-out",
              transformStyle: "preserve-3d",
            }}
          >
            <img
              src={VIAL_IMAGE}
              alt="Hexxium Research Peptide Vial"
              className="w-48 md:w-64 h-auto drop-shadow-[0_20px_60px_rgba(0,174,239,0.3)]"
              draggable={false}
              decoding="async"
              fetchPriority="high"
            />

            {/* Hotspot Markers */}
            {vialMarkers.map((marker) => (
              <motion.button
                key={marker.id}
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8 + marker.id * 0.2, type: "spring", stiffness: 200 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveMarker(activeMarker === marker.id ? null : marker.id);
                }}
                className={`absolute z-20 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 cursor-pointer ${
                  activeMarker === marker.id
                    ? "bg-[#00AEEF] text-white scale-125 shadow-[0_0_25px_rgba(0,174,239,0.7)]"
                    : "bg-white/90 text-[#06070A] hover:bg-[#00AEEF] hover:text-white shadow-lg hover:shadow-[0_0_20px_rgba(0,174,239,0.5)]"
                }`}
                style={{ top: marker.position.top, left: marker.position.left }}
                data-testid={`vial-marker-${marker.id}`}
              >
                {marker.id}
                {activeMarker !== marker.id && (
                  <span className="absolute inset-0 rounded-full border-2 border-[#00AEEF]/60 animate-ping" />
                )}
              </motion.button>
            ))}
          </motion.div>

          {/* Drag hint */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 text-slate-600 text-xs flex items-center gap-2"
          >
            <Sparkles className="w-3 h-3" />
            {t("vial_hover_hint")}
          </motion.div>
        </div>

        {/* Info Panel - 2 cols */}
        <div className="lg:col-span-2 space-y-4">
          {vialMarkers.map((marker) => (
            <motion.div
              key={marker.id}
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 + marker.id * 0.15 }}
              onClick={() => setActiveMarker(activeMarker === marker.id ? null : marker.id)}
              className={`glass rounded-xl p-5 cursor-pointer transition-all duration-300 border ${
                activeMarker === marker.id
                  ? "border-[#00AEEF]/50 bg-[#00AEEF]/5 shadow-[0_0_25px_rgba(0,174,239,0.1)]"
                  : "border-white/5 hover:border-white/15"
              }`}
              data-testid={`vial-info-${marker.id}`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 transition-colors ${
                  activeMarker === marker.id ? "bg-[#00AEEF] text-white" : "bg-white/10 text-[#00AEEF]"
                }`}>
                  {marker.id}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <marker.icon className={`w-4 h-4 flex-shrink-0 ${activeMarker === marker.id ? "text-[#00AEEF]" : "text-slate-400"}`} />
                    <h4 className="text-white font-semibold">{marker.label}</h4>
                  </div>
                  <AnimatePresence>
                    {activeMarker === marker.id && (
                      <motion.p
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="text-slate-400 text-sm mt-2 leading-relaxed overflow-hidden"
                      >
                        {marker.description}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Floating Promo Banners
const FloatingBanners = () => {
  const [showPromo, setShowPromo] = useState(false);
  const [promoDismissed, setPromoDismissed] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    const promoSeen = sessionStorage.getItem("promo_dismissed");
    if (!promoSeen) {
      const t1 = setTimeout(() => setShowPromo(true), 3000);
      return () => clearTimeout(t1);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissPromo = () => {
    setShowPromo(false);
    setPromoDismissed(true);
    sessionStorage.setItem("promo_dismissed", "1");
  };

  return (
    <>
      {/* 10% Off Promo Banner */}
      <AnimatePresence>
        {showPromo && !promoDismissed && (
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 100 }}
            className="fixed bottom-4 left-4 right-4 sm:right-auto sm:left-6 sm:bottom-6 z-50 sm:max-w-xs"
            data-testid="promo-banner"
          >
            <div className="glass-dark rounded-2xl p-5 border border-[#00AEEF]/30 shadow-[0_0_30px_rgba(0,174,239,0.15)]">
              <button
                onClick={dismissPromo}
                className="absolute top-3 right-3 text-slate-500 hover:text-white transition-colors"
                data-testid="promo-dismiss"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#00AEEF]/20 flex items-center justify-center flex-shrink-0">
                  <Tag className="w-5 h-5 text-[#00AEEF]" />
                </div>
                <div>
                  <div className="text-white font-bold">{t("promo_title")}</div>
                  <div className="text-slate-400 text-xs">{t("promo_subtitle")}</div>
                </div>
              </div>
              <p className="text-slate-400 text-sm mb-4">
                {t("promo_desc")}
              </p>
              <Link
                to="/register"
                onClick={dismissPromo}
                className="block w-full text-center py-2.5 bg-[#00AEEF] hover:bg-[#00AEEF]/90 text-white text-sm font-semibold rounded-xl transition-colors"
                data-testid="promo-cta"
              >
                {t("promo_cta")}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [batchResult, setBatchResult] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState("");
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const carouselRef = useRef(null);
  const { user } = useAuth();
  const { t } = useLang();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const productsRes = await apiGet(`${API}/products?featured=true`);
        setProducts(productsRes.data);
      } catch (firstErr) {
        // Products may fail if API is warming up; silently retry once.
        // eslint-disable-next-line no-console
        console.warn("[HomePage] featured products fetch failed, retrying:", firstErr);
        try {
          const retry = await apiGet(`${API}/products?featured=true`);
          setProducts(retry.data);
        } catch (retryErr) {
          // Non-critical: homepage will show without featured products.
          // eslint-disable-next-line no-console
          console.error("[HomePage] featured products retry failed:", retryErr);
        }
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBatchVerify = async (e) => {
    e.preventDefault();
    if (!batchId.trim()) return;
    
    setBatchLoading(true);
    setBatchError("");
    setBatchResult(null);

    try {
      const response = await axios.get(`${API}/batches/verify/${batchId}`);
      setBatchResult(response.data);
    } catch (error) {
      setBatchError(t("batch_not_found"));
    }
    setBatchLoading(false);
  };

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = direction === "left" ? -300 : 300;
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const features = [
    { icon: Shield, title: t("feature_1_title"), description: t("feature_1_desc") },
    { icon: FileCheck, title: t("feature_2_title"), description: t("feature_2_desc") },
    { icon: Search, title: t("feature_3_title"), description: t("feature_3_desc") },
    { icon: CreditCard, title: t("feature_4_title"), description: t("feature_4_desc") },
    { icon: Microscope, title: t("feature_5_title"), description: t("feature_5_desc") },
  ];

  const timelineSteps = [
    { icon: UserPlus, title: t("timeline_step_1_title"), description: t("timeline_step_1_desc") },
    { icon: ShoppingBag, title: t("timeline_step_2_title"), description: t("timeline_step_2_desc") },
    { icon: ClipboardCheck, title: t("timeline_step_3_title"), description: t("timeline_step_3_desc") },
    { icon: CreditCard, title: t("timeline_step_4_title"), description: t("timeline_step_4_desc") },
    { icon: Truck, title: t("timeline_step_5_title"), description: t("timeline_step_5_desc") },
  ];

  return (
    <div className="min-h-screen bg-[#06070A]" data-testid="home-page">
      <SEOHead path="/" title="Premium Research-Grade Peptides" description="Hexxium Research offers high-purity research-grade peptides with third-party testing, COA verification, and compliance-safe checkout. For research use only." />
      <Navbar />
      <FloatingBanners />

      {/* Promo / Announcement Banner */}
      <PromoBanner />

      {/* Hero Section */}
      <section className="relative py-10 md:py-14 flex items-center hero-gradient overflow-hidden">
        <DNABackground />
        
        {/* Large Logo Background Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden z-[1]">
          <motion.img
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.12, scale: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            src={LOGO_URL}
            alt=""
            className="w-[90%] md:w-[70%] lg:w-[60%] max-w-[900px] h-auto object-contain select-none"
            style={{ filter: "brightness(1.5) contrast(1.1)" }}
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <span className="inline-block px-4 py-2 bg-slate-300/[0.06] border border-slate-300/30 rounded-full text-sm font-semibold tracking-wider uppercase mb-6 metallic-text" data-testid="hero-badge">
                {t("hero_badge")}
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold leading-tight mb-6">
                <span className="metallic-text">{t("hero_title_1")}</span>
                <br />
                <span className="text-[#00AEEF] text-glow">{t("hero_title_2")}</span>
              </h1>
              <p className="text-base md:text-lg text-slate-400 leading-relaxed mb-8 max-w-lg">
                {t("hero_subtitle")}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to={user ? "/shop" : "/login"}
                  state={user ? undefined : { from: { pathname: "/shop" } }}
                  className="btn-primary flex items-center gap-2"
                  data-testid="hero-view-catalog-btn"
                >
                  <FlaskConical className="w-5 h-5" />
                  View Catalog
                </Link>
                <Link
                  to="/verify"
                  className="btn-secondary flex items-center gap-2"
                  data-testid="hero-verify-btn"
                >
                  <Shield className="w-5 h-5" />
                  {t("hero_cta_verify")}
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 sm:flex sm:gap-12 mt-12 pt-8 border-t border-white/10">
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-white">
                    <Counter end={99} suffix="%" />
                  </div>
                  <div className="text-slate-500 text-xs sm:text-sm">{t("stat_purity")}</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-white">
                    <Counter end={500} suffix="+" />
                  </div>
                  <div className="text-slate-500 text-xs sm:text-sm">{t("stat_batches")}</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-white">
                    <Counter end={50} suffix="+" />
                  </div>
                  <div className="text-slate-500 text-xs sm:text-sm">{t("stat_products")}</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center lg:block"
            >
              <div className="relative max-w-[280px] sm:max-w-none">
                <div className="absolute -inset-8 bg-[#00AEEF]/15 blur-[80px] rounded-full" />
                <div className="absolute -inset-4 bg-purple-500/10 blur-[60px] rounded-full" />
                <img
                  src={IMG_HERO_DNA}
                  alt="DNA molecular structure — precision research compounds"
                  className="relative rounded-2xl w-full scale-110 origin-center opacity-55"
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  width={940}
                  height={940}
                  style={{
                    objectFit: "cover",
                    maskImage: "radial-gradient(ellipse 78% 78% at center, black 42%, transparent 96%)",
                    WebkitMaskImage: "radial-gradient(ellipse 78% 78% at center, black 42%, transparent 96%)",
                  }}
                />
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-l from-transparent via-[#06070A]/25 to-[#06070A]/75" />
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#06070A]/55 via-transparent to-[#06070A]/40" />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1.5 h-1.5 rounded-full bg-[#00AEEF]"
            />
          </div>
        </motion.div>
      </section>

      {/* Trust Value Strip */}
      <TrustStrip />

      {/* Interactive 3D Vial Showcase */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#06070A] via-[#00AEEF]/[0.02] to-[#06070A]" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <AnimatedSection className="text-center mb-12">
            <span className="text-[#00AEEF] text-sm font-medium uppercase tracking-wider mb-4 block">
              {t("vial_section_badge")}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              {t("vial_section_title_1")} <span className="metallic-text">{t("vial_section_title_2")}</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {t("vial_section_subtitle")}
            </p>
          </AnimatedSection>

          <InteractiveVial />
        </div>
      </section>

      {/* Analytical Transparency — batch-verified COA showcase */}
      <AnalyticalTransparency />

      {/* Recently Viewed (renders only when the visitor has history) */}
      <RecentlyViewed contained />

      {/* Features Section */}
      <section className="py-16 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-[#00AEEF] text-sm font-medium uppercase tracking-wider mb-4 block">
              {t("features_badge")}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              {t("features_title_1")} <span className="metallic-text">{t("features_title_2")}</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {t("features_subtitle")}
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <FeatureCard key={feature.title} {...feature} delay={index * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* DNA Visual Divider */}
      <section className="relative h-[200px] md:h-[400px] overflow-hidden" data-testid="dna-divider">
        <img
          src={IMG_DNA_HELIX}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#06070A] via-[#06070A]/40 to-[#06070A]" />
        <div className="relative h-full flex items-center justify-center text-center px-6">
          <AnimatedSection>
            <p className="text-[#00AEEF] text-xs sm:text-sm font-medium uppercase tracking-[0.25em] mb-3">Research-Grade Compounds</p>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white max-w-2xl">Precision-Engineered at the Molecular Level</h3>
          </AnimatedSection>
        </div>
      </section>

      {/* About Hexxium Summary Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#00AEEF]/[0.02] to-transparent" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <span className="text-[#00AEEF] text-sm font-medium uppercase tracking-wider mb-4 block">
                {t("about_badge")}
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                {t("about_summary_title_1")} <span className="metallic-text">{t("about_summary_title_2")}</span> {t("about_summary_title_3")}
              </h2>
              <div className="space-y-4 text-slate-400 leading-relaxed">
                <p>{t("about_summary_p1")}</p>
                <p>{t("about_summary_p2")}</p>
                <p>{t("about_summary_p3")}</p>
              </div>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-[#00AEEF] hover:text-[#1AA8FF] font-medium mt-6 transition-colors group"
                data-testid="about-learn-more"
              >
                {t("about_learn_more")}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </AnimatedSection>

            <AnimatedSection className="relative">
              <div className="rounded-2xl overflow-hidden border border-white/10 relative aspect-[4/3]">
                <img
                  src={IMG_BRANDED_VIALS}
                  alt="Hexxium Research clean-room production line — sterile vial filling under aseptic conditions"
                  className="w-full h-full object-cover"
                  style={{ filter: "saturate(0.7) hue-rotate(-15deg) contrast(1.05) brightness(0.92)" }}
                  loading="lazy"
                  decoding="async"
                />
                {/* Cyan tone overlay to harmonize image with brand palette (#00AEEF) */}
                <div
                  className="absolute inset-0 mix-blend-color pointer-events-none"
                  style={{ background: "rgba(0, 174, 239, 0.28)" }}
                />
                {/* Subtle deep-cyan multiply for atmospheric depth */}
                <div
                  className="absolute inset-0 mix-blend-multiply pointer-events-none"
                  style={{ background: "linear-gradient(135deg, rgba(0,174,239,0.12) 0%, rgba(6,7,10,0.35) 100%)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#06070A] via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="glass-dark rounded-xl p-4 backdrop-blur-md border border-white/10">
                      <FileCheck className="w-6 h-6 text-[#00AEEF] mb-2" />
                      <h4 className="text-white font-semibold text-sm">{t("about_card_purity")}</h4>
                    </div>
                    <div className="glass-dark rounded-xl p-4 backdrop-blur-md border border-white/10">
                      <Shield className="w-6 h-6 text-[#00AEEF] mb-2" />
                      <h4 className="text-white font-semibold text-sm">{t("about_card_standards")}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-32 relative">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-[#00AEEF] text-sm font-medium uppercase tracking-wider mb-4 block">
              {t("timeline_badge")}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              {t("timeline_title_1")} <span className="metallic-text">{t("timeline_title_2")}</span>
            </h2>
          </AnimatedSection>

          <div className="space-y-0">
            {timelineSteps.map((step, index) => (
              <TimelineStep
                key={step.title}
                step={<step.icon className="w-5 h-5 text-white" />}
                title={step.title}
                description={step.description}
                index={index}
                isLast={index === timelineSteps.length - 1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Lab Equipment Visual Divider */}
      <section className="relative h-[200px] md:h-[360px] overflow-hidden" data-testid="lab-divider">
        <img
          src={IMG_LAB_EQUIPMENT}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#06070A] via-[#06070A]/50 to-[#06070A]" />
        <div className="relative h-full flex items-center justify-center text-center px-6">
          <AnimatedSection>
            <p className="text-[#00AEEF] text-xs sm:text-sm font-medium uppercase tracking-[0.25em] mb-3">Advanced Analytical Testing</p>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white max-w-2xl">Every Batch Verified by Independent Laboratories</h3>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-[#00AEEF]/10 via-transparent to-[#00AEEF]/10" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
          <AnimatedSection>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              {t("cta_title_1")} <span className="metallic-text">{t("cta_title_2")}</span>
            </h2>
            <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
              {t("cta_subtitle")}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register" className="btn-primary" data-testid="cta-register">
                {t("cta_register")}
              </Link>
              <Link to="/shop" className="btn-secondary" data-testid="cta-shop">
                {t("cta_shop")}
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Batch Verification Section */}
      <section className="py-16 md:py-32 relative">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative">
          <AnimatedSection className="text-center mb-12">
            <span className="text-[#00AEEF] text-sm font-medium uppercase tracking-wider mb-4 block">
              {t("batch_badge")}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              {t("batch_title_1")} <span className="metallic-text">{t("batch_title_2")}</span>
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              {t("batch_subtitle")}
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <div className="glass-dark rounded-2xl p-8 md:p-12">
              <form onSubmit={handleBatchVerify} className="mb-8">
                <div className="flex flex-col md:flex-row gap-4">
                  <input
                    type="text"
                    value={batchId}
                    onChange={(e) => setBatchId(e.target.value)}
                    placeholder={t("batch_placeholder")}
                    className="flex-1 verification-input rounded-xl px-6 py-4 text-white font-mono text-lg focus:outline-none"
                    data-testid="batch-verify-input"
                  />
                  <button
                    type="submit"
                    disabled={batchLoading}
                    className="btn-primary px-8 py-4 flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="batch-verify-submit"
                  >
                    {batchLoading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Search className="w-5 h-5" />
                        {t("batch_verify_btn")}
                      </>
                    )}
                  </button>
                </div>
              </form>

              {/* Result */}
              <AnimatePresence mode="wait">
                {batchError && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400"
                    data-testid="batch-error"
                  >
                    {batchError}
                  </motion.div>
                )}

                {batchResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="space-y-6"
                    data-testid="batch-result"
                  >
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                      <span className="text-green-500 font-semibold">{t("batch_verified")}</span>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-white/5 rounded-xl p-6">
                        <span className="text-slate-500 text-sm">{t("batch_product")}</span>
                        <p className="text-white text-xl font-semibold">{batchResult.product?.name}</p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-6">
                        <span className="text-slate-500 text-sm">{t("batch_purity")}</span>
                        <p className="text-[#00AEEF] text-xl font-bold">{batchResult.batch?.purity}%</p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-6">
                        <span className="text-slate-500 text-sm">{t("batch_test_date")}</span>
                        <p className="text-white font-mono">{batchResult.batch?.test_date}</p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-6">
                        <span className="text-slate-500 text-sm">{t("batch_laboratory")}</span>
                        <p className="text-white">{batchResult.batch?.lab_name}</p>
                      </div>
                    </div>

                    <button
                      className="w-full flex items-center justify-center gap-2 py-4 bg-[#00AEEF]/10 hover:bg-[#00AEEF]/20 border border-[#00AEEF]/30 rounded-xl text-[#00AEEF] font-medium transition-colors"
                      data-testid="download-coa"
                    >
                      <Download className="w-5 h-5" />
                      {t("batch_download_coa")}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Trusted Supplier Brand Banner */}
      <section className="py-16 md:py-32 relative" data-testid="brand-banner-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_60px_rgba(0,174,239,0.08)]">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/media/hexxium-trusted-supplier-banner.webp`}
                alt="Hexxium Research — Your Trusted Research Compound Supplier. cGMP certified facilities, third-party tested, COAs provided with every compound, U.S.-based operations, dedicated customer support."
                className="w-full h-auto block"
                loading="lazy"
                data-testid="brand-banner-image"
              />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Get in Touch */}
      <section className="py-16 md:py-28 relative" data-testid="get-in-touch-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-12">
            <span className="text-[#00AEEF] text-sm font-medium uppercase tracking-wider mb-4 block">Contact</span>
            <h2 className="text-3xl md:text-5xl font-bold text-white">
              Get in <span className="metallic-text">Touch</span>
            </h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-6">
            <AnimatedSection>
              <a href="mailto:info@hexxiumresearch.com"
                className="glass rounded-2xl p-8 flex flex-col items-center text-center h-full hover:border-[#00AEEF]/40 border border-transparent transition-colors group"
                data-testid="contact-email-card">
                <div className="w-14 h-14 rounded-2xl bg-[#00AEEF]/10 border border-[#00AEEF]/20 flex items-center justify-center mb-5 group-hover:bg-[#00AEEF]/20 transition-colors">
                  <Mail className="w-7 h-7 text-[#00AEEF]" />
                </div>
                <h3 className="text-white font-semibold mb-2">Email</h3>
                <p className="text-slate-400 text-sm group-hover:text-[#00AEEF] transition-colors">info@hexxiumresearch.com</p>
              </a>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <a href="tel:+19544173321"
                className="glass rounded-2xl p-8 flex flex-col items-center text-center h-full hover:border-[#00AEEF]/40 border border-transparent transition-colors group"
                data-testid="contact-phone-card">
                <div className="w-14 h-14 rounded-2xl bg-[#00AEEF]/10 border border-[#00AEEF]/20 flex items-center justify-center mb-5 group-hover:bg-[#00AEEF]/20 transition-colors">
                  <Phone className="w-7 h-7 text-[#00AEEF]" />
                </div>
                <h3 className="text-white font-semibold mb-2">Customer Support</h3>
                <p className="text-slate-400 text-sm group-hover:text-[#00AEEF] transition-colors">954-417-3321</p>
              </a>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="glass rounded-2xl p-8 flex flex-col items-center text-center h-full"
                data-testid="contact-address-card">
                <div className="w-14 h-14 rounded-2xl bg-[#00AEEF]/10 border border-[#00AEEF]/20 flex items-center justify-center mb-5">
                  <MapPin className="w-7 h-7 text-[#00AEEF]" />
                </div>
                <h3 className="text-white font-semibold mb-2">Address</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  #1013 4543 N. Pine Island Rd.<br />Sunrise, FL 33351<br />United States
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <Footer />

      {/* Quick View Modal */}
      <AnimatePresence>
        {quickViewProduct && (
          <QuickViewModal
            product={quickViewProduct}
            onClose={() => setQuickViewProduct(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
